import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import AuthPage from "../../components/auth/AuthPage";
import OtpField from "../../components/auth/OtpField";
import PasswordInput from "../../components/auth/PasswordInput";
import PasswordStrength from "../../components/auth/PasswordStrength";
import SubmitButton from "../../components/auth/SubmitButton";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { API } from "../../constant/api";
import { useUser } from "../../context/user-context";
import { resetPasswordSchema } from "../../utils/authSchemas";
import { errorMessage } from "../../utils/errorMessage";
import sessionAxios from "../../utils/sessionAxios";

type ResetPasswordValues = {
  otp: string;
  password: string;
  confirmPassword: string;
};

const ResetPassword = () => {
  const { resetEmail } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const form = useForm<ResetPasswordValues>({
    resolver: yupResolver(resetPasswordSchema) as any,
    defaultValues: { otp: "", password: "", confirmPassword: "" },
    mode: "onTouched",
  });

  const password = form.watch("password");

  const onSubmit = (values: ResetPasswordValues) => {
    if (loading) return;
    setLoading(true);

    sessionAxios
      .post(API.resetPassword, {
        otp: values.otp,
        email: resetEmail,
        newPassword: values.password,
      })
      .then(() => {
        setLoading(false);
        toast.success("Password changed successfully");
        navigate("/login");
      })
      .catch((e: any) => {
        setLoading(false);
        toast.error(
          errorMessage(
            e?.response?.data?.message || "An error occurred. Please try again"
          )
        );
      });
  };

  return (
    <AuthPage
      title="Reset your password"
      description={
        resetEmail
          ? `Enter the code sent to ${resetEmail} and choose a new password.`
          : "Enter the code sent to your email and choose a new password."
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <FormField
            control={form.control}
            name="otp"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Verification code</FormLabel>
                <FormControl>
                  <OtpField value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    placeholder="Enter a new password"
                    {...field}
                  />
                </FormControl>
                <PasswordStrength password={password} />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    placeholder="Re-enter your new password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <SubmitButton loading={loading}>Reset password</SubmitButton>
        </form>
      </Form>
    </AuthPage>
  );
};

export default ResetPassword;
